// src/components/admin/FinalCustomersTargeted.jsx
// Reusable component for showing final customer list with all exclusions

import React, { useState } from 'react';
import ExclusionPreviewModal from './ExclusionPreviewModal';
import {
  getCustomersAfterUnsubscribeExclusion,
  getCustomersAfterFrequencyExclusion,
  getCustomersAfterRecurrenceExclusion,
  getFinalCustomerList
} from '../../api/campaignExclusionService';

function FinalCustomersTargeted({
  salonId,
  campaignType,
  formData,
  targetingCriteriaSaved,
  disabled = false
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(''); // 'unsubscribe', 'frequency', 'recurrence', 'final'
  const [previewData, setPreviewData] = useState(null);

  const campaignTypeNames = {
    email: 'Email',
    whatsapp: 'WhatsApp',
    push: 'Push Notification',
    social_media: 'Social Media'
  };

  const displayName = campaignTypeNames[campaignType] || campaignType;

  const handlePreview = async (type) => {
    if (!targetingCriteriaSaved) {
      setError('Please save targeting criteria first before previewing exclusions');
      setTimeout(() => setError(''), 3000);
      return;
    }

    try {
      setLoading(true);
      setError('');
      let data;

      const targetingOptions = {
        targetingType: formData.targetingType,
        targetingCriteria: formData.targetingCriteria
      };

      const recurringSettings = formData.schedulingType === 'recurring'
        ? formData.recurring
        : null;

      switch(type) {
        case 'unsubscribe':
          data = await getCustomersAfterUnsubscribeExclusion(
            salonId,
            campaignType,
            targetingOptions
          );
          break;
        case 'frequency':
          data = await getCustomersAfterFrequencyExclusion(
            salonId,
            campaignType,
            targetingOptions
          );
          break;
        case 'recurrence':
          data = await getCustomersAfterRecurrenceExclusion(
            salonId,
            campaignType,
            targetingOptions,
            recurringSettings
          );
          break;
        case 'final':
          data = await getFinalCustomerList(
            salonId,
            campaignType,
            targetingOptions,
            recurringSettings
          );
          break;
        default:
          throw new Error('Invalid preview type');
      }

      setPreviewData(data);
      setModalType(type);
      setShowModal(true);
    } catch (err) {
      console.error(`Error getting ${type} preview:`, err);
      setError(`Failed to load preview: ${err.message}`);
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const isRecurringCampaign = formData.schedulingType === 'recurring' &&
                              formData.recurring?.excludePreviousRecipients;

  return (
    <div className={`final-customers-section ${disabled ? 'disabled-section' : ''}`}>
      <h3>Final Customers Targeted</h3>
      <p className="final-customers-description">
        Preview the final list of customers who will receive this {displayName.toLowerCase()} campaign
        after applying all exclusion rules (frequency limits and recurring campaign settings).
      </p>

      {error && <div className="error-message">{error}</div>}

      <div className="final-customers-buttons">
        {/* Button 1: Unsubscribe Exclusion Preview */}
        <button
          type="button"
          className="preview-exclusion-button"
          onClick={() => handlePreview('unsubscribe')}
          disabled={disabled || loading || !targetingCriteriaSaved}
        >
          <span className="button-icon">✉️</span>
          <div className="button-content">
            <span className="button-title">
              Preview List After Excluding {displayName} Unsubscribe
            </span>
            <span className="button-description">
              Shows customers eligible after removing those who have unsubscribed
            </span>
          </div>
          <span className="button-arrow">→</span>
        </button>

        {/* Button 2: Frequency Exclusion Preview */}
        <button
          type="button"
          className="preview-exclusion-button"
          onClick={() => handlePreview('frequency')}
          disabled={disabled || loading || !targetingCriteriaSaved}
        >
          <span className="button-icon">🚫</span>
          <div className="button-content">
            <span className="button-title">
              Preview List After Excluding Frequency Limits
            </span>
            <span className="button-description">
              Shows customers eligible after removing those who reached frequency caps
            </span>
          </div>
          <span className="button-arrow">→</span>
        </button>

        {/* Button 3: Recurrence Exclusion Preview (only for recurring campaigns) */}
        {isRecurringCampaign && (
          <button
            type="button"
            className="preview-exclusion-button"
            onClick={() => handlePreview('recurrence')}
            disabled={disabled || loading || !targetingCriteriaSaved}
          >
            <span className="button-icon">🔄</span>
            <div className="button-content">
              <span className="button-title">
                Preview List After Excluding Recipients from Earlier Runs
              </span>
              <span className="button-description">
                Shows customers eligible after removing previous campaign recipients
              </span>
            </div>
            <span className="button-arrow">→</span>
          </button>
        )}

        {/* Button 4: Final Nett List */}
        <button
          type="button"
          className="preview-exclusion-button final-list-button"
          onClick={() => handlePreview('final')}
          disabled={disabled || loading || !targetingCriteriaSaved}
        >
          <span className="button-icon">🎯</span>
          <div className="button-content">
            <span className="button-title">
              Final Nett List After All Exclusions
            </span>
            <span className="button-description">
              Final list of customers who will receive the campaign (used for Actual Blast)
            </span>
          </div>
          <span className="button-arrow">✓</span>
        </button>
      </div>

      {!targetingCriteriaSaved && (
        <div className="final-customers-info">
          <strong>Note:</strong> Please save your targeting criteria first to enable preview functionality.
        </div>
      )}

      <div className="final-customers-info">
        <strong>Important:</strong> The "Final Nett List" represents the exact customers who will
        receive your campaign when you click "Send Actual Blast". This list excludes: (i) customers
        who have unsubscribed from {displayName.toLowerCase()} communications, (ii) customers who have
        reached frequency limits, and (iii) recipients of previous campaign runs (for recurring campaigns).
      </div>

      {/* Preview Modal */}
      <ExclusionPreviewModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        previewData={previewData}
        modalType={modalType}
        campaignName={formData.subject || `${displayName} campaign`}
      />
    </div>
  );
}

export default FinalCustomersTargeted;
